/**
 * lorenzPipeline — Lorenz attractor traced in real-time.
 *
 * The Lorenz attractor is a 3D chaotic trajectory governed by:
 *   dx/dt = sigma * (y - x)
 *   dy/dt = x * (rho - z) - y
 *   dz/dt = x * y - beta * z
 *
 * With sigma=10, rho=28, beta=8/3 the system is famously chaotic —
 * the trajectory never repeats but stays bounded in a fractal
 * "butterfly" region of phase space.
 *
 * We compute the trajectory for many initial conditions and trace
 * them through the screen as glowing curves. Each point's color
 * encodes its z-coordinate (height in phase space). The attractor's
 * iconic two-wing structure emerges naturally.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface LorenzPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// Integrate the Lorenz system using a simple Euler step. Returns
// the trajectory as a series of points.
void lorenzStep(inout vec3 p, inout vec3 prev) {
  const float sigma = 10.0;
  const float rho = 28.0;
  const float beta = 8.0 / 3.0;
  float dt = 0.008;
  float dx = sigma * (p.y - p.x);
  float dy = p.x * (rho - p.z) - p.y;
  float dz = p.x * p.y - beta * p.z;
  prev = p;
  p += vec3(dx, dy, dz) * dt;
}

// Project 3D Lorenz coords to 2D screen. We orbit the camera around
// the Y axis and project orthographically.
vec2 project(vec3 p, float angle) {
  float ca = cos(angle), sa = sin(angle);
  vec3 rotated = vec3(ca * p.x - sa * p.z, p.y, sa * p.x + ca * p.z);
  // Center the attractor (it spans roughly x,z in [-30, 30]).
  rotated = rotated * 0.025;
  // Shift to put the focus in the upper portion.
  rotated.y += 0.05;
  return rotated.xy;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected screen coords.
  vec2 screenP = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);

  // Camera angle orbits slowly with time.
  float camAngle = uTime * 0.15 + bass * 0.5;

  // Compute trajectory. We integrate from an initial condition, with
  // a slight offset so different pixels see different trajectories
  // (creating a "depth" of attractors).
  vec3 p = vec3(0.1 + 0.5 * hash21(gl_FragCoord.xy), 0.0, 0.0);
  vec3 prev = p;
  vec3 accumColor = vec3(0.0);
  float accumDensity = 0.0;
  // March a fixed number of steps and accumulate color along the path.
  // We sample points along the trajectory at a few step intervals.
  const int STEPS = 240;
  for (int i = 0; i < STEPS; i++) {
    if (i >= 240) break;
    // The integration speed scales with audio.
    float dt = 1.0 + 2.0 * mid;
    for (int j = 0; j < 3; j++) {
      lorenzStep(p, prev);
    }
    // Project this 3D point to screen.
    vec2 proj = project(p, camAngle);
    // Distance from current pixel to this point on the trajectory.
    float d = length(screenP - proj);
    // Accumulate a soft glow at this point.
    float glow = exp(-d * d * 800.0);
    // Color based on z-coordinate (height in phase space).
    vec3 col;
    float zNorm = clamp((p.z + 5.0) / 50.0, 0.0, 1.0);
    if (zNorm < 0.5) {
      col = mix(vec3(0.05, 0.10, 0.40), vec3(0.30, 0.85, 0.95), zNorm * 2.0);
    } else {
      col = mix(vec3(0.30, 0.85, 0.95), vec3(1.0, 0.40, 0.20), (zNorm - 0.5) * 2.0);
    }
    // Audio: brighter on high freq.
    col *= 1.0 + high * 0.5;
    accumColor += col * glow;
    accumDensity += glow;
  }
  vec3 col = accumColor;

  // Background: deep dark with subtle radial fade.
  vec3 bg = vec3(0.02, 0.03, 0.06);
  col += bg;

  // Vignette.
  vec2 vp = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float vig = 1.0 - dot(vp, vp) * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeLorenzBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.4);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.6 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 0.9 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.1 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.3 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.6 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.0 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.4 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 2.9 + 3.0);
}

export function computeLorenzRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createLorenzPipeline(gl: WebGL2RenderingContext): LorenzPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawLorenz(
  gl: WebGL2RenderingContext,
  pipe: LorenzPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
